import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import useI18n from '@/hooks/useI18n'
import { RightArrowIcon } from '@/assets/icons'
import { questionDetailUrl, keepQueryString } from '@/urls'
import { getLocalePrefix } from '@/util/localePath'
import { countdownParts, formatPercent, pad2 } from '@/features/brazil-elections/utils'
import UsStateMap from '@/features/midterm-map/UsStateMap'
import { MIDTERMS_ELECTION_DATE } from '@/constants/midterms'
import { MIDTERM_EVENT } from './midtermEvent'
import useSenateControl from './useSenateControl'
import MidtermBackdrop from './MidtermBackdrop'
import { BANNER_MAP_BY_STATE } from './bannerMapSnapshot'
import { ShimmerBlock } from './Shimmer'
import democraticLogo from '@/assets/midterms/democratic-logo.png'
import republicanLogo from '@/assets/midterms/republican-logo.png'

const FIGURES = 'font-[family-name:var(--font-figures)]'
/* The scoreboard's eyebrow: small, spaced wide, set in the figures face. */
const EYEBROW = `${FIGURES} text-[11px] leading-4 font-semibold uppercase tracking-[0.24em]`

/* Same glass as the midterm control cards: a thin white rim and a light
   sheen over a see-through slab, so the split field shows through. */
const INK = 'text-Neutral-6 [html[data-theme=\'dark\']_&]:text-white'
const MUTED = 'text-Neutral-4 [html[data-theme=\'dark\']_&]:text-white/75'
const FAINT = 'text-Neutral-4/80 [html[data-theme=\'dark\']_&]:text-white/55'

const GLASS =
  'relative isolate overflow-hidden rounded-2xl border border-Neutral-3 bg-surface-2 shadow-[0_12px_32px_rgba(0,0,0,0.08)] [html[data-theme=\'dark\']_&]:border-white/[0.14] [html[data-theme=\'dark\']_&]:bg-white/[0.08] [html[data-theme=\'dark\']_&]:backdrop-blur-xl [html[data-theme=\'dark\']_&]:backdrop-saturate-150 [html[data-theme=\'dark\']_&]:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_18px_50px_rgba(0,0,0,0.28)]'
const SHEEN =
  'pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(165deg,rgba(255,255,255,0.55)_0%,rgba(255,255,255,0.08)_36%,transparent_62%)] [html[data-theme=\'dark\']_&]:bg-[linear-gradient(165deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.03)_36%,transparent_62%)]'

/** The parties' round seals, white discs that read as badges on the dark board. */
const PARTY_LOGOS = { democratic: democraticLogo, republican: republicanLogo }

/** Keeps a visitor on the localized route tree, the way SearchWidget links do. */
const useLocaleHref = () => {
  const { pathname } = useLocation()
  return path => `${getLocalePrefix(pathname)}${keepQueryString(path)}`
}

const Star = ({ className = 'size-2 shrink-0' }) => (
  <svg viewBox="0 0 10 10" className={className} aria-hidden="true">
    <path
      d="M5 0l1.18 3.45h3.58L6.9 5.59l1.09 3.46L5 6.95 2.01 9.05 3.1 5.59.24 3.45h3.58L5 0z"
      fill="currentColor"
    />
  </svg>
)

const DuelSkeleton = ({ hero }) => (
  <div className="flex flex-col gap-4">
    <div className="flex items-end justify-between">
      <ShimmerBlock className={`rounded ${hero ? 'h-14 w-32' : 'h-12 w-28'}`} />
      <ShimmerBlock className={`rounded ${hero ? 'h-14 w-32' : 'h-12 w-28'}`} />
    </div>
    <ShimmerBlock className="h-2.5 w-full rounded-full" />
  </div>
)

/** Snapshot-coloured US map. No tag fetch — the homepage only paints this.
   The wrapper is a receding plane hinged from the bottom-left, so Alaska
   sits in the near corner and the rest of the country fades back. */
export const BannerMap = ({ className = '', tilt = false }) => (
  <div
    className={`pointer-events-none [--midterm-empty-fill:rgba(15,40,52,0.1)] [html[data-theme='dark']_&]:[--midterm-empty-fill:rgba(255,255,255,0.16)] ${className}`}
    style={
      tilt
        ? {
            transform: 'perspective(1400px) rotateX(30deg) rotateY(-16deg)',
            transformOrigin: '0% 100%',
            filter: 'blur(0.3px) drop-shadow(0 28px 22px rgba(0,0,0,0.45))',
          }
        : undefined
    }
    aria-hidden="true">
    <UsStateMap byState={BANNER_MAP_BY_STATE} decorative revealOnMount={false} />
  </div>
)

/** Live pill plus the election-day clock, the same units as the map page. */
const BannerCountdown = () => {
  const i18n = useI18n()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const parts = countdownParts(MIDTERMS_ELECTION_DATE, now)
  const units = parts.finished
    ? null
    : [
        { value: String(parts.days), label: i18n('midterm_map.countdown_days') },
        { value: pad2(parts.hours), label: i18n('midterm_map.countdown_hours') },
        { value: pad2(parts.minutes), label: i18n('midterm_map.countdown_minutes') },
        { value: pad2(parts.seconds), label: i18n('midterm_map.countdown_seconds') },
      ]

  return (
    <div className="flex flex-wrap items-center gap-3">
      {units ? (
        <div className="flex items-start gap-1.5" role="timer" aria-label={i18n('midterm_map.election_day')}>
          {units.map((unit, index) => (
            <React.Fragment key={unit.label}>
              {index > 0 && <span className={`${FAINT} pt-0.5 text-sm font-black`}>:</span>}
              <span className="flex flex-col items-center">
                <span className={`${FIGURES} ${INK} text-lg font-black leading-none tabular-nums`}>{unit.value}</span>
                <span className={`${FAINT} mt-0.5 text-[8px] font-semibold uppercase tracking-[0.18em]`}>
                  {unit.label}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <span className={`${FIGURES} ${INK} text-sm font-black uppercase tracking-[0.14em]`}>
          {i18n('midterm_map.election_day')}
        </span>
      )}
    </div>
  )
}

/**
 * The featured market as a scoreboard: Democrats on the left against
 * Republicans on the right, each quote printed as the market has it, over a
 * bar splitting the two against each other with the majority line notched at
 * its centre. The seal sits on the figure, the party name above it.
 */
const SenateScoreboard = ({ hero, control }) => {
  const i18n = useI18n()
  const localeHref = useLocaleHref()
  const { market, sides, share, loading, isLive } = control

  /* The skeleton stands only for the detail fetch in flight; once the hook has
     settled without a board the banner above is gone entirely. */
  if (!sides && !loading) return null

  let body = <DuelSkeleton hero={hero} />
  if (sides) {
    const [left, right] = sides
    const figure = `${FIGURES} ${INK} font-black leading-none tabular-nums tracking-[-0.04em] ${
      hero ? 'text-[clamp(36px,3.6vw,52px)]' : 'text-[34px]'
    }`
    body = (
      <div className="relative flex flex-col gap-4">
        <div className="relative flex items-end justify-between gap-6">
          {sides.map((side, index) => {
            const logo = PARTY_LOGOS[side.party]
            const animal = logo && (
              <img src={logo} alt="" aria-hidden="true" className={`shrink-0 ${hero ? 'size-9' : 'size-7'}`} />
            )
            return (
              <div
                key={side.party}
                className={`flex min-w-0 flex-col gap-2 ${index === 1 ? 'items-end text-right' : 'items-start'}`}
              >
                <span className={`${EYEBROW} max-w-full truncate`} style={{ color: side.labelColor }}>
                  {side.label}
                </span>
                <div className="flex items-center gap-2.5">
                  {index === 0 && animal}
                  <span className={figure}>{formatPercent(side.probability)}</span>
                  {index === 1 && animal}
                </div>
              </div>
            )
          })}
        </div>
        <div className="relative pt-4">
          <span
            className={`${FIGURES} ${FAINT} absolute left-1/2 top-0 -translate-x-1/2 text-[9px] font-semibold uppercase tracking-[0.2em]`}
          >
            {i18n('midterm_banner.majority')}
          </span>
          <div
            className="relative flex h-2.5 w-full gap-1"
            role="img"
            aria-label={`${left.label} ${formatPercent(left.probability)} · ${right.label} ${formatPercent(
              right.probability
            )}`}
          >
            <span
              className="h-full rounded-full transition-[width] duration-700"
              style={{ width: `${share}%`, background: left.color }}
            />
            <span className="h-full flex-1 rounded-full" style={{ background: right.color }} />
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-Neutral-5 [html[data-theme='dark']_&]:bg-white/70"
            />
          </div>
        </div>
      </div>
    )
  }

  const className = `${GLASS} flex w-full flex-col ${hero ? 'p-5 md:p-6' : 'p-4'}`
  const sheen = <span aria-hidden="true" className={SHEEN} />

  /* The app's unlayered `a { color: inherit }` wins over Tailwind's utilities,
     so the link's colour is set inline to survive it. */
  if (sides && market && isLive) {
    return (
      <Link
        to={localeHref(questionDetailUrl(market))}
        style={{ color: 'inherit' }}
        className={`${className} ${INK} no-underline transition-colors hover:bg-Neutral-2 [html[data-theme='dark']_&]:hover:bg-white/[0.11]`}
      >
        {sheen}
        {body}
      </Link>
    )
  }

  return (
    <div className={className}>
      {sheen}
      {body}
    </div>
  )
}

const Header = ({ event, hero }) => {
  const i18n = useI18n()

  return (
    <div className="flex min-w-0 flex-col items-center gap-2.5 text-center">
      <span className={`${EYEBROW} ${MUTED} flex items-center gap-2.5`}>
        <Star className="size-2 shrink-0 text-[#3340F0] [html[data-theme='dark']_&]:text-[#97A3FF]" />
        {i18n(event.eyebrowKey)}
        <Star className="size-2 shrink-0 text-[#E03131] [html[data-theme='dark']_&]:text-[#FF8F8F]" />
      </span>
      <h2
        className={`m-0 ${INK} font-black leading-[1.02] tracking-[-0.03em] ${
          hero ? 'text-[clamp(28px,3vw,42px)]' : 'text-[26px]'
        }`}
      >
        {i18n(event.titleKey)}
      </h2>
      <p className={`m-0 ${MUTED} text-sm leading-5 ${hero ? 'max-w-[48ch]' : 'max-w-[52ch]'}`}>
        {i18n(event.subtitleKey)}
      </p>
    </div>
  )
}

const Ctas = ({ event, hero }) => {
  const i18n = useI18n()
  const localeHref = useLocaleHref()

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
      {event.ctas.map(cta => (
        <Link
          key={cta.id}
          to={localeHref(cta.to)}
          /* The app ships unlayered `a { color: inherit }` and `a { text-decoration:
             none }`, and Tailwind's utilities sit in `@layer utilities`, which
             loses to them, so both have to be set here to survive. */
          style={{
            color: cta.primary ? 'var(--midterm-cta-ink)' : 'var(--midterm-cta-ghost)',
            ...(cta.primary ? {} : { textDecoration: 'underline', textUnderlineOffset: '5px' }),
          }}
          className={`inline-flex h-11 items-center gap-2 text-sm font-bold transition-colors [--midterm-cta-ink:#ffffff] [--midterm-cta-ghost:var(--color-Neutral-5)] [html[data-theme='dark']_&]:[--midterm-cta-ink:#0b0a14] [html[data-theme='dark']_&]:[--midterm-cta-ghost:#ffffff] ${
            cta.primary
              ? 'rounded-full bg-Neutral-5 px-5 hover:bg-Neutral-6 [html[data-theme=\'dark\']_&]:bg-white [html[data-theme=\'dark\']_&]:hover:bg-white/90'
              : 'decoration-Neutral-3 hover:decoration-Neutral-5 [html[data-theme=\'dark\']_&]:decoration-white/35 [html[data-theme=\'dark\']_&]:hover:decoration-white'
          }`}
        >
          {i18n(cta.labelKey)}
          <RightArrowIcon width="6" height="11" aria-hidden="true" className="fill-current" />
        </Link>
      ))}
    </div>
  )
}

/**
 * The midterms banner. `hero` is the home strip's opening slide, which sits
 * on the backdrop the carousel paints across the whole banner; without it the
 * banner is a self-contained card for the feed, carrying its own ground.
 */
const MidtermBanner = ({ hero = false, isActive = true }) => {
  const i18n = useI18n()
  const control = useSenateControl()
  const event = MIDTERM_EVENT
  const label = `${i18n(event.eyebrowKey)} — ${i18n(event.titleKey)}`

  /* No open market to put on the board — the fetch errored, the outcomes carry
     no party pair, or the market has closed or resolved — so the whole banner
     goes, statement and CTA included, rather than announcing a settled event. */
  if (control.hidden) return null

  if (hero) {
    return (
      <div
        className="home-highlight__slide home-highlight__slide--event relative overflow-hidden"
        role="group"
        aria-label={label}
        aria-hidden={!isActive}
        inert={!isActive}
      >
        <div className="relative z-10 flex h-full min-h-0 w-full flex-col items-center justify-center gap-5">
          <div className="absolute right-0 top-0">
            <BannerCountdown />
          </div>
          <Header event={event} hero />
          <div className="w-full min-w-[280px] max-w-[520px]">
            <SenateScoreboard hero control={control} />
          </div>
          <Ctas event={event} hero />
        </div>
      </div>
    )
  }

  return (
    <article
      aria-label={label}
      className={`relative isolate flex h-full w-full flex-col items-center gap-5 overflow-hidden rounded-lg border border-Neutral-2 p-5 ${INK}`}
    >
      <MidtermBackdrop compact className="-z-10" />
      <BannerMap tilt className="absolute -bottom-[22%] left-0 w-[125%] opacity-40" />
      <div className="relative z-10 flex w-full flex-col items-center gap-5">
        <Header event={event} />
        <BannerCountdown />
        <SenateScoreboard control={control} />
        <Ctas event={event} />
      </div>
    </article>
  )
}

export default MidtermBanner
